const CACHE_NAME = 'design-ai-models-v1';

export interface DownloadProgress {
  /** Bytes received so far. */
  loaded: number;
  /** Total bytes, or `null` when the server omits `Content-Length`. */
  total: number | null;
  /** 0..1, or `null` when the total is unknown. */
  fraction: number | null;
  fromCache: boolean;
}

export interface FetchModelOptions {
  onProgress?: (progress: DownloadProgress) => void;
  signal?: AbortSignal;
  /** Skip the cache lookup and re-download. */
  force?: boolean;
}

/**
 * Downloads a model, caching it in the Cache API.
 *
 * Weights are 50-180 MB and the workbench is used in bursts, so a cold reload
 * must not re-download. The Cache API is the right store here: it survives
 * reloads, is evicted under storage pressure like any other cache, and unlike
 * IndexedDB it keeps the bytes as an opaque `Response` with no structured-clone
 * cost on read.
 */
export async function fetchModel(url: string, options: FetchModelOptions = {}): Promise<ArrayBuffer> {
  const cache = await openCache();

  if (cache && !options.force) {
    const hit = await cache.match(url);
    if (hit) {
      const buffer = await hit.arrayBuffer();
      options.onProgress?.({
        loaded: buffer.byteLength,
        total: buffer.byteLength,
        fraction: 1,
        fromCache: true,
      });
      return buffer;
    }
  }

  const response = await fetch(url, { signal: options.signal, mode: 'cors' });
  if (!response.ok) {
    throw new Error(`[inference] failed to download ${url}: ${response.status} ${response.statusText}`);
  }

  const total = parseLength(response.headers.get('content-length'));
  const buffer = await readWithProgress(response, total, options.onProgress);

  if (cache) {
    try {
      await cache.put(
        url,
        new Response(buffer.slice(0), {
          headers: {
            'content-type': 'application/octet-stream',
            'content-length': String(buffer.byteLength),
          },
        }),
      );
    } catch {
      // Quota exceeded or private mode: the model still works this session.
    }
  }
  return buffer;
}

/** Bytes currently held for each cached model URL. */
export async function listCachedModels(): Promise<string[]> {
  const cache = await openCache();
  if (!cache) return [];
  return (await cache.keys()).map((request) => request.url);
}

export async function evictModel(url: string): Promise<boolean> {
  const cache = await openCache();
  return cache ? cache.delete(url) : false;
}

export async function clearModelCache(): Promise<void> {
  if (typeof caches !== 'undefined') await caches.delete(CACHE_NAME);
}

async function openCache(): Promise<Cache | null> {
  if (typeof caches === 'undefined') return null;
  try {
    return await caches.open(CACHE_NAME);
  } catch {
    return null; // Private browsing modes can refuse.
  }
}

function parseLength(value: string | null): number | null {
  if (!value) return null;
  const parsed = Number.parseInt(value, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

async function readWithProgress(
  response: Response,
  total: number | null,
  onProgress?: (progress: DownloadProgress) => void,
): Promise<ArrayBuffer> {
  if (!response.body || !onProgress) return response.arrayBuffer();

  const reader = response.body.getReader();
  const chunks: Uint8Array[] = [];
  let loaded = 0;

  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    if (!value) continue;
    chunks.push(value);
    loaded += value.byteLength;
    onProgress({
      loaded,
      total,
      fraction: total ? Math.min(1, loaded / total) : null,
      fromCache: false,
    });
  }

  const out = new Uint8Array(loaded);
  let offset = 0;
  for (const chunk of chunks) {
    out.set(chunk, offset);
    offset += chunk.byteLength;
  }
  return out.buffer;
}
